import { graphql } from 'react-relay';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';
import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
import Button from '@atlaskit/button/button';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import {
	CHANGE_PAGE_OWNER_EXPERIENCE,
	CONTENT_OWNERSHIP_DIALOG_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceSuccess } from '@confluence/experience-tracker/entry-points/component.ts';
import { usePageSpaceKey, usePageContentId, usePageState } from '@confluence/page-context';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';
import { useSpaceManagementOperations } from '@confluence/space-utils/entry-points/graphql.ts';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import type { OptionData } from '@confluence/user-and-group-search/entry-points/user-picker.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { isRelayEnabled } from '@confluence/apollo-relay-interop/entry-points/isRelayEnabled.ts';
import { mutateInterop } from '@confluence/apollo-relay-interop/entry-points/mutate.ts';

import type {
	ContentOwnershipDialogContentOwnerHistoryQuery as ContentOwnershipDialogContentOwnerHistoryQuery$data,
	ContentOwnershipDialogContentOwnerHistoryQueryVariables as ContentOwnershipDialogContentOwnerHistoryQuery$variables,
} from './__types__/ContentOwnershipDialogContentOwnerHistoryQuery.ts';
import type { UpdateOwnerMutation as UpdateOwnerMutationType } from './queries/__types__/UpdateOwnerMutation.ts';
import { i18n } from './i18n.ts';
import { FlexContainer, ModalLabel, ModalLabelEl } from './ContentOwnershipStyles.tsx';
import { EXPECTED_ERROR_MESSAGES } from './contentOwnershipConstants.ts';
import { UpdateOwnerMutation } from './queries/UpdateOwnerMutation.graphql.ts';
// eslint-disable-next-line import/no-cycle
import { useContentOwnershipContext } from './ContentOwnershipContext.tsx';
import { ContentOwnershipDialogUserPicker } from './ContentOwnershipDialogUserPicker.tsx';
import { ContentOwnershipPermissionWarning } from './ContentOwnershipPermissionWarning.tsx';
import { checkIsUserRestricted, useExperienceTrackerContext } from './content-ownership-helpers.ts';
import type { Owner, Restrictions } from './ownership-models.ts';
import { ChangeOwnerConfirmationModal } from './ChangeOwnerConfirmationModal.tsx';
import { useIsSpaceOrSiteAdmin } from './useIsSpaceOrSiteAdmin.ts';
import { refreshDocumentContributors } from './DocumentContributorsRefresh.ts';

const ContentOwnershipDialogRefetchOnMutationQuery = graphql`
	query ContentOwnershipDialogRefetchOnMutationQuery($contentId: ID!) {
		contentContributors(id: $contentId) {
			isOwnerContributor
		}
	}
`;
const ContentOwnershipDialogContentOwnerHistoryQuery = graphql`
	query ContentOwnershipDialogContentOwnerHistoryQuery($contentId: ID!) {
		singleContent(id: $contentId) {
			id
			history {
				createdDate
				createdBy {
					displayName
					type
					... on KnownUser {
						accountId
					}
					profilePicture {
						path
					}
				}
				ownedBy {
					displayName
					type
					... on KnownUser {
						accountId
					}
					profilePicture {
						path
					}
				}
			}
		}
	}
`;

const currentOwnerContainer = xcss({
	marginBottom: 'space.100',
});

const photoAndNameContainer = xcss({
	display: 'flex',
	justifyContent: 'flex-start',
	alignItems: 'center',
	marginBottom: 'space.200',
});

const nameContainer = xcss({
	marginLeft: 'space.075',
});

type ContentOwnershipDialogComponentProps = WithFlagsProps & {
	shouldReturnFocusRef?: RefObject<HTMLElement>;
};

const ContentOwnershipDialogComponent = ({
	flags,
	shouldReturnFocusRef,
}: ContentOwnershipDialogComponentProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isLivePage = useIsCurrentPageLive();

	const intl = useIntl();
	const experienceTracker = useExperienceTrackerContext();
	const [spaceKey] = usePageSpaceKey();
	const [contentId] = usePageContentId();
	const [{ contentType }] = usePageState();
	const { Modal } = useChoreographerAssets();

	const [selectedUser, setSelectedUser] = useState<OptionData | undefined>(undefined);
	const [currentOwner, setCurrentOwner] = useState<Owner | undefined>(undefined);
	const [isRequestInFlight, setIsRequestInFlight] = useState<boolean>(false);
	const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);
	const [restrictions, setRestrictions] = useState<Restrictions>({
		isUserRestricted: false,
		isSpaceRestricted: false,
		isParentPageRestricted: false,
		isEditPageRestricted: false,
	});
	const { closeModal, onOwnerChange } = useContentOwnershipContext();
	const isAdmin = useIsSpaceOrSiteAdmin();
	const { isRbacEnabled, loading: isLoadingSpaceRoleMode } = useSpaceRoleMode();
	const { canManageContent, loading: isLoadingSpaceManagementOperations } =
		useSpaceManagementOperations({ spaceKey: spaceKey ?? '', skip: !spaceKey });
	const canBypassConfirmModal = isRbacEnabled ? canManageContent : isAdmin;
	const { data } = useQueryInterop<
		ContentOwnershipDialogContentOwnerHistoryQuery$data,
		ContentOwnershipDialogContentOwnerHistoryQuery$variables
	>(ContentOwnershipDialogContentOwnerHistoryQuery, {
		variables: {
			contentId: contentId!,
		},
		skip: !contentId,
	});

	useEffect(() => {
		if (!data) {
			return;
		}

		let ownerId: string | undefined;
		const ownerData = data?.singleContent?.history?.ownedBy;

		if (ownerData && 'accountId' in ownerData) {
			ownerId = ownerData.accountId ?? undefined;
		}

		const owner = {
			fullName: ownerData?.displayName ?? undefined,
			avatarUrl: ownerData?.profilePicture?.path ?? undefined,
			id: ownerId,
		};
		setCurrentOwner(owner);
	}, [data]);

	useEffect(() => {
		const { isUserRestricted, isSpaceRestricted, isParentPageRestricted } = restrictions;

		if (isUserRestricted || isSpaceRestricted || isParentPageRestricted) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'changeErrorPermissionWarning',
					actionSubject: 'content.owner',
					source: 'changeOwnerModal',
					attributes: {
						...restrictions,
						contentId,
						isAdmin,
						contentType,
					},
				},
			}).fire();
		}
	}, [restrictions, createAnalyticsEvent, contentId, isAdmin, contentType]);

	const resetRestrictions = () => {
		setRestrictions({
			isUserRestricted: false,
			isSpaceRestricted: false,
			isParentPageRestricted: false,
			isEditPageRestricted: false,
		});
	};

	const recheckSelectedUserAccess = useCallback(async () => {
		if (!selectedUser?.id) {
			setRestrictions({
				isUserRestricted: false,
				isSpaceRestricted: false,
				isParentPageRestricted: false,
				isEditPageRestricted: false,
			});
			return;
		}
		const newRestrictions = await checkIsUserRestricted(
			contentId,
			selectedUser.id,
			'network-only',
		).catch((error: unknown) => {
			// We intentionally log only the error class name and a network-error flag
			// instead of error.message. The backend resolver for userWithContentRestrictions
			// interpolates the user's accountId (AAID) into NotFoundException messages
			// (e.g. "User not found: 557058:..."). Whether AGG strips these before they
			// reach the client is unverified, so we treat error.message as potentially
			// containing PII. Logging raw error messages in analytics is an auto-SEV2
			// risk per Atlassian policy — we classify the error type instead.
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'recheckUserAccess',
					source: 'changeOwnerModal',
					attributes: {
						contentId,
						contentType,
						errorName: error instanceof Error ? error.name : 'UnknownError',
						isNetworkError: error instanceof Error && 'networkError' in error,
					},
				},
			}).fire();
			void flags.showErrorFlag({
				id: 'content-ownership-recheck-access-failed',
				title: intl.formatMessage(i18n.recheckAccessFailedFlagMessage),
				isAutoDismiss: true,
			});
			return undefined;
		});
		if (newRestrictions) {
			setRestrictions(newRestrictions);
		}
	}, [contentId, selectedUser, contentType, createAnalyticsEvent, flags, intl]);

	const handleShowConfirmModal = useCallback(() => {
		setShowConfirmModal(true);
	}, []);

	const handleHideConfirmModal = useCallback(() => {
		setShowConfirmModal(false);
	}, []);

	// TODO: The user picker itself doesn't seem to get cleared
	const handleClearSelectedUser = useCallback(() => {
		setSelectedUser(undefined);
		resetRestrictions();
	}, []);

	const handleSelectUser = useCallback(
		async (user: any) => {
			setIsRequestInFlight(true);
			setSelectedUser(user);
			const newRestrictions = await checkIsUserRestricted(contentId, user.id);
			if (newRestrictions) {
				setRestrictions(newRestrictions);
			} else {
				resetRestrictions();
			}
			setIsRequestInFlight(false);
			if (expValEquals('a11y-fixes-week4-may-2026', 'isEnabled', true)) {
				// Restore focus to the combobox input after the user selection closes the dropdown
				// This prevents focus from incorrectly moving to the modal Close button (WCAG 3.2.2)
				setTimeout(() => {
					if (typeof document !== 'undefined') {
						const input = document.getElementById(
							'react-select-content.owner-input',
						) as HTMLInputElement | null;
						input?.focus();
					}
				}, 0);
			}
		},
		[contentId],
	);

	const tryUpdateOwner = useCallback(async () => {
		try {
			experienceTracker.start({
				name: CHANGE_PAGE_OWNER_EXPERIENCE,
			});
			setIsRequestInFlight(true);
			const newOwnerId = selectedUser!.id;

			try {
				const mutationResult = await mutateInterop<UpdateOwnerMutationType>({
					mutation: UpdateOwnerMutation,
					variables: {
						contentId,
						ownerId: newOwnerId,
					},
					refetchQueries: [
						{
							query: ContentOwnershipDialogRefetchOnMutationQuery,
							variables: { contentId },
						},
						{
							query: ContentOwnershipDialogContentOwnerHistoryQuery,
							variables: { contentId },
						},
					],
				});
				if (
					contentId &&
					!isLivePage &&
					(contentType === 'page' || contentType === 'blogpost') &&
					isRelayEnabled('@confluence/view-page-common') &&
					!StatsigConfigurations.isKillSwitchOn('confluence_vp_title_byline_frag_dedup') &&
					!StatsigConfigurations.getBoolean('confluence_vp_contributors_frag_reader', true) &&
					expVal('confluence_exp_vp_document', 'isEnabled', false)
				) {
					// A refresh failure must not report an already successful ownership change as failed.
					await refreshDocumentContributors(contentId).catch(() => {
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								action: 'refreshError',
								actionSubject: 'content.contributors',
								source: 'changeOwnerModal',
								attributes: { contentId },
							},
						}).fire();
					});
				}
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'changeSuccess',
						actionSubject: 'content.owner',
						source: 'changeOwnerModal',
						attributes: {
							contentId,
							isAdmin,
							isLivePage,
							contentType,
						},
					},
				}).fire();
				experienceTracker.succeed({
					name: CHANGE_PAGE_OWNER_EXPERIENCE,
				});
				onOwnerChange?.(currentOwner?.id ?? '', newOwnerId);
				closeModal();
				void flags.showSuccessFlag({
					id: 'content-ownership-dialog-update-owner-success',
					title: intl.formatMessage(i18n.successFlagMessage, {
						user: mutationResult?.data?.updateOwner?.content?.history?.ownedBy?.displayName,
					}),
					isAutoDismiss: true,
				});
			} catch (error) {
				// if the attempt to update owner fails due to
				// new owner lacking page view / edit permissions
				if (error.message.includes(EXPECTED_ERROR_MESSAGES.USER_MISSING_PAGE_PERMISSION)) {
					experienceTracker.abort({
						name: CHANGE_PAGE_OWNER_EXPERIENCE,
						reason: 'New owner does not have view/edit permission on the page',
					});
					setRestrictions({ ...restrictions, isUserRestricted: true });
				} else {
					experienceTracker.fail({
						name: CHANGE_PAGE_OWNER_EXPERIENCE,
						error,
					});
				}
				createAnalyticsEvent({
					type: 'sendTrackEvent',
					data: {
						action: 'changeError',
						actionSubject: 'content.owner',
						source: 'changeOwnerModal',
						attributes: {
							...error,
							contentId,
							isAdmin,
							isLivePage,
							contentType,
						},
					},
				}).fire();
				void flags.showErrorFlag({
					id: 'content-ownership-dialog-update-owner-failed',
					title: intl.formatMessage(i18n.errorFlagMessage),
					isAutoDismiss: true,
				});
			}
		} finally {
			setIsRequestInFlight(false);
		}
	}, [
		selectedUser,
		contentId,
		closeModal,
		restrictions,
		createAnalyticsEvent,
		isAdmin,
		experienceTracker,
		isLivePage,
		flags,
		intl,
		contentType,
		onOwnerChange,
		currentOwner?.id,
	]);

	const handleConfirmChange = useCallback(async () => {
		setShowConfirmModal(false);
		await tryUpdateOwner();
	}, [tryUpdateOwner]);

	const onChangeButtonHandler = useMemo(() => {
		return canBypassConfirmModal ? tryUpdateOwner : handleShowConfirmModal;
	}, [canBypassConfirmModal, tryUpdateOwner, handleShowConfirmModal]);

	if (!spaceKey || !contentId) {
		return null;
	}

	if (!currentOwner) {
		return null;
	}

	return (
		<>
			<Modal
				testId="content-owner-modal"
				onClose={closeModal}
				messageType="transactional"
				messageId="content-ownership-modal"
				shouldReturnFocus={shouldReturnFocusRef}
			>
				<ModalHeader>
					<ModalTitle>{intl.formatMessage(i18n.modalTitleEnabledForAllContent)}</ModalTitle>
					<CloseButton onClick={closeModal} label={intl.formatMessage(i18n.closeModal)} />
				</ModalHeader>
				<ModalBody>
					<Box xcss={currentOwnerContainer}>
						<ModalLabel>{intl.formatMessage(i18n.modalCurrentOwner)}</ModalLabel>
						<Box xcss={photoAndNameContainer}>
							<Avatar
								src={currentOwner?.avatarUrl}
								name={
									expValEquals('a11y-fixes-week4-may-2026', 'isEnabled', true)
										? undefined
										: currentOwner?.fullName
								}
								size="small"
							/>
							<Box xcss={nameContainer} as="span">
								{currentOwner?.fullName}
							</Box>
						</Box>
					</Box>
					{expValEquals('a11y-fixes-week4-may-2026', 'isEnabled', true) ? (
						<ModalLabelEl
							htmlFor="react-select-content.owner-input"
							data-testid="content-owner-modal--new-owner-label"
						>
							{intl.formatMessage(i18n.modalBodyEnabledForAllContent)}
						</ModalLabelEl>
					) : (
						<ModalLabel data-testid="content-owner-modal--new-owner-label">
							{intl.formatMessage(i18n.modalBodyEnabledForAllContent)}
						</ModalLabel>
					)}
					<ContentOwnershipDialogUserPicker
						selectedUser={selectedUser}
						onSelectUser={handleSelectUser}
						onClearSelectedUser={handleClearSelectedUser}
						spaceKey={spaceKey}
						contentId={contentId}
						currentOwner={currentOwner}
						ariaLabel={intl.formatMessage(i18n.modalBodyEnabledForAllContent)}
					/>
					{restrictions.isUserRestricted && (
						<ContentOwnershipPermissionWarning
							isParentPageRestriction={restrictions.isParentPageRestricted}
							isEditPageRestricted={restrictions.isEditPageRestricted}
							contentId={contentId}
							contentType={contentType}
							spaceKey={spaceKey}
							isSpaceRestricted={restrictions.isSpaceRestricted}
							selectedUser={selectedUser}
							resetRestrictions={resetRestrictions}
							recheckAccess={recheckSelectedUserAccess}
						/>
					)}
				</ModalBody>
				<ModalFooter>
					<FlexContainer>
						{/* <HiddenInput autoFocus /> */}
						<Button appearance="subtle" onClick={closeModal}>
							{intl.formatMessage(i18n.modalCancel)}
						</Button>
						<Button
							testId="content-owner-modal-update-owner-button"
							appearance="primary"
							onClick={onChangeButtonHandler}
							isDisabled={
								!selectedUser ||
								restrictions.isUserRestricted ||
								isRequestInFlight ||
								isLoadingSpaceRoleMode ||
								isLoadingSpaceManagementOperations
							}
						>
							{intl.formatMessage(i18n.modalChangeOwner)}
						</Button>
					</FlexContainer>
				</ModalFooter>
			</Modal>
			{showConfirmModal && (
				<ChangeOwnerConfirmationModal
					onCancel={handleHideConfirmModal}
					onConfirm={handleConfirmChange}
					currentOwner={currentOwner}
					newOwner={{
						fullName: selectedUser!.name,
						avatarUrl: selectedUser!.avatarUrl,
						id: selectedUser!.id,
					}}
				/>
			)}
			<ExperienceSuccess
				name={CONTENT_OWNERSHIP_DIALOG_EXPERIENCE}
				attributes={{
					contentType,
					isLivePage,
				}}
			/>
		</>
	);
};

export const ContentOwnershipDialog: React.ComponentType<
	Omit<
		{
			shouldReturnFocusRef?: RefObject<HTMLElement>;
		},
		'flags'
	>
> = withFlags(({ flags, shouldReturnFocusRef }: ContentOwnershipDialogComponentProps) => {
	const { isModalOpen } = useContentOwnershipContext();

	if (!isModalOpen) {
		return null;
	}

	return (
		<ContentOwnershipDialogComponent flags={flags} shouldReturnFocusRef={shouldReturnFocusRef} />
	);
});
